import { GetStaticProps } from "next"
import Image from "next/image"
import { Grid, Box, Container } from "@mui/material"

import { makeStyles } from "makeStyles"

import SEO from "components/SEO"
import Link from "components/i18n/Link"
import Button from "components/Buttons/Button"
import serializeJSON from "@secureo/common/utils/serializeJSON"

import {
	fetchCategoryPreviews,
	CategoryPreview as CategoryPreviewI,
} from "utils/commerceTools/fetchCategory"
import { anonymousAccessTokenCache } from "@secureo/common/utils/commerceTools/token/accessTokenCache"

const useStyles = makeStyles()((theme) => ({
	item: {
		color: "white",
		backgroundColor: theme.palette.secondary.light,
		padding: "30px 40px",
		marginBottom: "3rem",
		alignItems: "center",
		"@media only screen and (max-width: 600px)": {
			flexDirection: "column-reverse",
			background: "none",
			padding: 0,
		},
	},
	responsiveBg: {
		backgroundColor: theme.palette.secondary.light,
		marginTop: -50,
		zIndex: 2,
		"@media only screen and (min-width: 600px)": {
			background: "none",
		},
	},
	image: {
		textAlign: "center",
	},
}))

const asPath = `/c`

const CategoryPreview = ({ categoryPreview }: SeriesPreviewProps) => {
	const { name, slug, image, shortDescription } = categoryPreview
	const { classes } = useStyles()

	return (
		<Link key={slug} href={`/c/${slug}`}>
			<Grid container spacing={3} className={classes.item}>
				<Grid item xs={12} sm={8} className={classes.responsiveBg}>
					<h2 style={{ marginTop: 30 }}>{name}</h2>
					<Box my={3}>
						<p>{shortDescription}</p>
					</Box>
					<div style={{ display: "flex" }}>
						<Button color="primary" style={{ width: 200, marginRight: 10 }}>
							Mehr erfahren
						</Button>
					</div>
				</Grid>
				<Grid item xs={12} sm={4} className={classes.image}>
					{image && (
						<Image
							alt={name}
							src={image}
							width={200}
							height={200}
							style={{
								maxWidth: "100%",
								height: "auto",
							}}
						/>
					)}
				</Grid>
			</Grid>
		</Link>
	)
}

interface SeriesPreviewProps {
	categoryPreview: CategoryPreviewI
}

const CategoryOverviewPage = ({ categories }: Props) => {
	return (
		<Box py={5} style={{ background: "#182F37", color: "white" }}>
			<SEO
				asPath={asPath}
				title={"Kategorien"}
				appendLocalizedShopNameWithPipe
				description="Alle Produkte von Secureo im Überblick. Finden Sie Ihren Tresor und durchstöbern Sie die verschiedenen Modelle!"
			/>
			<Container>
				<h1>Kategorien</h1>

				<div>
					{categories.map((category) => {
						return <CategoryPreview key={category.slug} categoryPreview={category} />
					})}
				</div>
			</Container>
		</Box>
	)
}

export const getStaticProps: GetStaticProps = async (context) => {
	const accessToken = await anonymousAccessTokenCache()
	const locale = context.locale as string

	const categories = await fetchCategoryPreviews(accessToken)

	const props = {
		locale,
		categories,
	}

	return serializeJSON({ props, revalidate: 10 })
}

interface Props {
	locale: string
	categories: CategoryPreviewI[]
}

export default CategoryOverviewPage
